// Which language the portal opens in.
//
// In order: a deep link (?lang=zh-CN, ?language=cn, …), the choice the user
// saved with the language switcher, the browser's preferred languages, English.
// A deep link is saved like a switcher choice, so it survives the login
// round trip, and is then dropped from the address bar.

import { DEFAULT_LOCALE, type Locale } from './locales';

export const LOCALE_PARAMS = ['lang', 'language', 'locale'];

// A language tag or a common spelling of one: zh, cn, zh-cn, zh_CN, zh-Hans,
// zh-Hans-CN, chinese, 中文 → zh-CN; en, en-US, english → en. Null otherwise.
// Traditional Chinese (zh-TW, zh-HK, zh-Hant) is not zh-CN.
export function localeFromTag(tag: string | null | undefined): Locale | null {
  const t = (tag ?? '').trim().toLowerCase().replace(/_/g, '-');
  if (!t) return null;
  if (t === 'cn' || t === 'chinese' || t === '中文' || t === '简体中文' || t === 'zh-cn' || t === 'zh-sg' || t === 'zh') return 'zh-CN';
  if (t.startsWith('zh-hans')) return 'zh-CN';
  if (t === 'en' || t === 'english' || t.startsWith('en-')) return 'en';
  return null;
}

export function initialLocale(opts: { search: string; stored: string | null; languages: readonly string[] }): { locale: Locale; fromLink: boolean } {
  const params = new URLSearchParams(opts.search);
  for (const p of LOCALE_PARAMS) {
    const l = localeFromTag(params.get(p));
    if (l) return { locale: l, fromLink: true };
  }
  const stored = opts.stored === 'en' || opts.stored === 'zh-CN' ? opts.stored : null;
  if (stored) return { locale: stored, fromLink: false };
  for (const lang of opts.languages) {
    const l = localeFromTag(lang);
    if (l) return { locale: l, fromLink: false };
  }
  return { locale: DEFAULT_LOCALE, fromLink: false };
}

// The address without the language parameters (other parameters and the hash kept).
export function withoutLocaleParams(href: string): string {
  const url = new URL(href);
  for (const p of LOCALE_PARAMS) url.searchParams.delete(p);
  return `${url.pathname}${url.search}${url.hash}`;
}
